import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { FaChevronDown, FaBars, FaTimes, FaUser } from 'react-icons/fa';
import styled from 'styled-components';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import Button from './Button';
import LaaniLogo from '../Assets/LaaniLogo.png';

const Navbar = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dropdownRefs = useRef([]);
  const mobileMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  const navItems = [
    {
      name: 'Explore',
      icon: '🔍',
      links: [
        { name: 'Buy Crypto', path: '/buy-crypto', icon: '💰' },
        { name: 'Blog', path: '/blogs', icon: '📝' },
        { name: 'Crypto Prices', path: '/coins', icon: '📊' },
      ],
    },
    {
      name: 'Dashboard',
      icon: '📊',
      links: [
        { name: 'My Dashboard', path: '/Dashboard', icon: '👀' },
        { name: 'Coins', path: '/dashboard/coins', icon: '🪙' },
        { name: 'Crypto Converter', path: '/dashboard/profile', icon: '🔄' },
        { name: 'My Watchlist', path: '/dashboard/coins/watchlist', icon: '⭐' },
      ],
    },
    {
      name: 'Institutional',
      icon: '🏢',
      links: [
        { name: 'Treasury Management', path: '/institutional', icon: '💼' },
        { name: 'Financial Tokenization', path: '/institutional', icon: '🎯' },
        { name: 'OTC/Trade', path: '/institutional', icon: '🤝' },
        { name: 'Custody', path: '/institutional', icon: '🔒' },
        { name: 'Payments', path: '/institutional', icon: '💳' },
      ],
    },
    {
      name: 'Company',
      icon: '🏪',
      links: [
        { name: 'Legal', path: '/legal', icon: '⚖️' },
        { name: 'About us', path: '/AboutUs', icon: '🌟' },
        { name: 'Help Center', path: '/help-center', icon: '❓' },
        { name: 'Connect with us', path: '/contactUs', icon: '📞' },
        { name: 'Feedback', path: '/feedback', icon: '💬' },
        { name: 'Investors', path: '/investors', icon: '👥' },
        { name: 'Research', path: '/research', icon: '🔬' },
      ],
    },
  ];

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMobileMenuOpen]);
  
  // Firebase auth listener
  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
    });
    return () => unsubscribe();
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);

  const isMobile = () => window.innerWidth <= 1080;

  const handleDropdownToggle = (index) => {
    if (isMobile()) {
      setActiveDropdown(activeDropdown === index ? null : index);
    }
  };

  const handleLinkClick = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
    setActiveDropdown(null);
  };

  const handleSignIn = () => {
    setIsMobileMenuOpen(false);
    navigate('/sign-in');
  };

  const handleSignOut = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      setUser(null);
      setIsUserMenuOpen(false);
      setIsMobileMenuOpen(false);
      navigate('/sign-in');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getFirstName = (user) => {
    if (user?.displayName) {
      return user.displayName.split(' ')[0];
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const handleClickOutside = (event) => {
    // Close dropdowns
    if (
      dropdownRefs.current.length > 0 &&
      dropdownRefs.current.every(
        (ref) => ref && !ref.contains(event.target)
      )
    ) {
      setTimeout(() => setActiveDropdown(null), 100);
    }
    
    // Close user menu
    if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
      setIsUserMenuOpen(false);
    }
    
    // Close mobile menu only if clicking outside the entire nav area
    if (
      mobileMenuRef.current &&
      !mobileMenuRef.current.contains(event.target) &&
      !event.target.closest('.mobile-menu-icon')
    ) {
      setIsMobileMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      {isMobileMenuOpen && <Overlay onClick={() => setIsMobileMenuOpen(false)} />}
      <Nav $scrolled={scrolled} $isMobileMenuOpen={isMobileMenuOpen}>
        <div className="nav-container">
          <div className="brand">
            <Link to="/" onClick={handleLinkClick}>
              <img src={LaaniLogo} alt="Laani Logo" className="logo" />
            </Link>
          </div>

          <div
            className={`nav-links ${isMobileMenuOpen ? 'mobile-open' : ''}`}
            ref={mobileMenuRef}
          >
            <div className="nav-items">
              {navItems.map((item, index) => (
                <div key={index} className="nav-item">
                  <div
                    className="nav-link"
                    onClick={() => handleDropdownToggle(index)}
                    onMouseEnter={() => !isMobile() && setActiveDropdown(index)}
                    onMouseLeave={() => !isMobile() && setActiveDropdown(null)}
                  >
                    <span className="nav-text">
                      <span className="nav-icon">{item.icon}</span>
                      {item.name}
                    </span>
                    <FaChevronDown className={`chevron ${activeDropdown === index ? 'rotated' : ''}`} />
                  </div>
                  {activeDropdown === index && (
                    <ul
                      className={`dropdown ${activeDropdown === index ? 'active' : ''}`}
                      ref={(el) => (dropdownRefs.current[index] = el)}
                      onMouseEnter={() => !isMobile() && setActiveDropdown(index)}
                      onMouseLeave={() => !isMobile() && setActiveDropdown(null)}
                    >
                      {item.links.map((link, idx) => (
                        <li key={idx} className="dropdown-item">
                          <Link to={link.path} onClick={handleLinkClick}>
                            <span className="link-icon">{link.icon}</span>
                            {link.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
            
            <div className="auth">
              {user ? (
                <div className="user-menu" ref={userMenuRef}>
                  <div className="user-info" onClick={toggleUserMenu}>
                    <div className="user-avatar">
                      {user.photoURL ? (
                        <img src={user.photoURL} alt="User" />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                    <span className="username">Hi, {getFirstName(user)}</span>
                    <FaChevronDown className={`user-chevron ${isUserMenuOpen ? 'rotated' : ''}`} />
                  </div>
                  {isUserMenuOpen && (
                    <ul className="user-dropdown">
                      <li onClick={handleSignOut}>
                        <span>🚪</span> Sign Out
                      </li>
                    </ul>
                  )}
                </div>
              ) : (
                <Button text="Sign In" onClick={handleSignIn} />
              )}
            </div>
          </div>

          <button className="mobile-menu-icon" onClick={toggleMobileMenu} aria-label="Toggle menu">
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </Nav>
    </>
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  backdrop-filter: blur(4px);
  z-index: 998;
  
  @media screen and (min-width: 1081px) {
    display: none;
  }
`;

const Nav = styled.nav`
  position: sticky;
  z-index: 1000;
  background: transparent;
  backdrop-filter: blur(20px);
  margin: 0 2rem;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);

  .nav-container {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem 2.5rem;
  }

  .brand {
    .logo {
      height: 100px;
      width: auto;
      transition: transform 0.3s ease;
      
      &:hover {
        transform: scale(1.05);
      }
    }
  }

  .nav-links {
    display: flex;
    align-items: center;
    gap: 3rem;

    .nav-items {
      display: flex;
      align-items: center;
      gap: 2.5rem;
    }

    .nav-item {
      position: relative;

      .nav-link {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        cursor: pointer;
        color: #fff;
        padding: 0.75rem 1rem;
        border-radius: 12px;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        font-weight: 500;
        font-size: 0.95rem;

        .nav-text {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .nav-icon {
          font-size: 1.1rem;
        }

        .chevron {
          font-size: 0.8rem;
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          
          &.rotated {
            transform: rotate(180deg);
          }
        }

        &:hover {
          background: rgba(26, 115, 232, 0.1);
          color: #1a73e8;
          transform: translateY(-2px);
        }
      }
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 0.5rem);
      left: 0;
      background: rgba(44, 62, 80, 0.95);
      backdrop-filter: blur(20px);
      border-radius: 16px;
      border: 1px solid rgba(255, 255, 255, 0.1);
      box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
      list-style: none;
      padding: 0.75rem;
      margin: 0;
      min-width: 220px;
      z-index: 1001;
      opacity: 0;
      visibility: hidden;
      transform: translateY(-10px);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

      &.active {
        opacity: 1;
        visibility: visible;
        transform: translateY(0);
      }

      .dropdown-item {
        border-radius: 12px;
        transition: all 0.3s ease;

        &:hover {
          background: rgba(52, 73, 94, 0.8);
          transform: translateX(4px);
        }

        a {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          color: #fff;
          text-decoration: none;
          padding: 0.875rem 1rem;
          border-radius: 12px;
          font-weight: 400;
          transition: color 0.3s ease;

          .link-icon {
            font-size: 1rem;
            opacity: 0.8;
          }

          &:hover {
            color: #1a73e8;
          }
        }
      }
    }

    .nav-item:hover .dropdown {
      opacity: 1;
      visibility: visible;
      transform: translateY(0);
    }
  }

  .auth {
    display: flex;
    align-items: center;

    .user-menu {
      position: relative;

      .user-info {
        display: flex;
        align-items: center;
        gap: 0.75rem;
        cursor: pointer;
        color: #fff;
        padding: 0.5rem 1rem;
        border-radius: 12px;
        transition: all 0.3s ease;
        background: rgba(26, 115, 232, 0.1);
        border: 1px solid rgba(26, 115, 232, 0.2);

        &:hover {
          background: rgba(26, 115, 232, 0.2);
          transform: translateY(-2px);
        }

        .user-avatar {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          background: linear-gradient(135deg, #1a73e8, #4285f4);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          svg {
            font-size: 0.9rem;
            color: white;
          }
        }

        .username {
          font-weight: 500;
          font-size: 0.95rem;
        }

        .user-chevron {
          font-size: 0.8rem;
          transition: transform 0.3s ease;
          
          &.rotated {
            transform: rotate(180deg);
          }
        }
      }

      .user-dropdown {
        position: absolute;
        top: calc(100% + 0.5rem);
        right: 0;
        background: rgba(44, 62, 80, 0.95);
        backdrop-filter: blur(20px);
        border-radius: 12px;
        border: 1px solid rgba(255, 255, 255, 0.1);
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        list-style: none;
        padding: 0.5rem;
        margin: 0;
        min-width: 160px;
        z-index: 1001;

        li {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1rem;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.3s ease;
          color: #fff;
          font-weight: 400;

          &:hover {
            background: rgba(231, 76, 60, 0.1);
            color: #e74c3c;
          }
        }
      }
    }
  }

  .mobile-menu-icon {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #fff;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.1);
      transform: scale(1.1);
    }
  }

  @media screen and (max-width: 1080px) {
    top: 0;
    margin: 0;
    border-radius: 0;
    border: none;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    .nav-container {
      padding: 1rem 1.5rem;
    }

    .nav-links {
      position: fixed;
      top: 0;
      right: 0;
      width: 85%;
      max-width: 380px;
      height: 100vh;
      background: linear-gradient(135deg, 
        rgba(26, 26, 26, 0.98) 0%, 
        rgba(44, 62, 80, 0.98) 100%);
      backdrop-filter: blur(20px);
      flex-direction: column;
      padding: 4rem 1.5rem 2rem;
      gap: 2rem;
      transform: translateX(100%);
      transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 999;
      overflow-y: auto;
      border-left: 1px solid rgba(255, 255, 255, 0.1);

      &.mobile-open {
        transform: translateX(0);
      }

      .nav-items {
        flex-direction: column;
        width: 100%;
        gap: 0;
        margin-top: 2rem;
      }

      .nav-item {
        width: 100%;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);

        &:last-child {
          border-bottom: none;
        }

        .nav-link {
          justify-content: space-between;
          padding: 1.25rem 0;
          border-radius: 0;
          font-size: 1.1rem;
          font-weight: 500;

          &:hover {
            background: transparent;
            color: #1a73e8;
            transform: none;
          }
        }
      }

      .dropdown {
        position: static;
        background: transparent;
        backdrop-filter: none;
        border: none;
        box-shadow: none;
        border-radius: 0;
        opacity: 1;
        visibility: visible;
        transform: none;
        padding: 0;
        margin-top: 0.5rem;

        .dropdown-item {
          &:hover {
            background: rgba(26, 115, 232, 0.1);
            transform: none;
          }

          a {
            padding: 0.75rem 1.5rem;
            font-size: 1rem;
            color: rgba(255, 255, 255, 0.8);

            &:hover {
              color: #1a73e8;
            }
          }
        }
      }

      .auth {
        margin-top: auto;
        width: 100%;

        .user-menu .user-info {
          justify-content: center;
          padding: 1rem;
          background: rgba(26, 115, 232, 0.15);
        }
      }
    }

    .mobile-menu-icon {
      display: block;
      z-index: 1001;
      position: absolute;
      top: 1.5rem;
      right: 1.5rem;
    }
  }

  @media screen and (max-width: 480px) {
    .nav-links {
      width: 100%;
      max-width: none;
    }

    .brand .logo {
      height: 40px;
    }
  }
`;

export default Navbar;